import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { Button, CircularProgress, Snackbar, Alert } from '@mui/material';

/**
 * A button component that allows users to manage their Stripe subscription
 * This opens the Stripe Customer Portal where they can view, update, or cancel their subscription
 */
const ManageSubscriptionButton = ({ businessId, variant = "contained", color = "primary", size = "medium", startIcon }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleManageSubscription = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get the current user's ID if businessId is not provided
      const userId = businessId || currentUser?.uid;

      if (!userId) {
        throw new Error('No user or business ID available');
      }

      // Call the Cloud Function to create a Stripe Portal session
      const functions = getFunctions();
      const createPortalSession = httpsCallable(functions, 'createStripePortalSession');

      const result = await createPortalSession({
        businessId: userId,
        returnUrl: window.location.href // Return to the current page after managing subscription
      });

      if (result.data.success && result.data.url) {
        // Open the Stripe Portal in a new tab
        window.open(result.data.url, '_blank');
        setSuccess(true);
      } else {
        throw new Error(result.data.error || 'Failed to create portal session');
      }
    } catch (error) {
      console.error('Error creating Stripe Portal session:', error);
      setError(error.message || 'An error occurred while trying to manage your subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(null);
  };

  return (
    <>
      <Button
        variant={variant}
        color={color}
        size={size}
        onClick={handleManageSubscription}
        disabled={loading || !currentUser}
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : startIcon}
      >
        {loading ? 'Loading...' : 'Manage Subscription'}
      </Button>

      {/* Success message */}
      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success">
          Stripe Customer Portal opened in a new tab
        </Alert>
      </Snackbar>

      {/* Error message */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ManageSubscriptionButton;
